import React, { useState, useEffect, useCallback, useMemo, FC } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEqual, pickBy } from 'lodash-es';
import { useEventListener } from 'hooks';
import { RootState } from 'store';
import { AnyObject } from 'types/Common';
import { Dictionary } from 'types/FilterValue';
import { getMethods } from 'creators/paymentMethods';
import getCustomSelectItems from 'creators/getCustomSelectItems';
import { openModal } from 'actions/modal';
import { loadDictionary } from 'api/dictionaries';
import { createMoto } from 'api/payouts';
import Utils from 'helpers/Utils';
import Messages from 'constants/rpcTypes';
import { fieldsConfig } from './fieldsConfig';
import { getRequiredFields } from 'components/formFields/helpers';
import showNotification from 'components/ui/notification/showNotification';
import FormFields from 'components/formFields';
import FormFieldsProps from 'components/formFields/FormFieldsProps';
import formStateFactory from 'components/formFields/formStateFactory';
import { RouteLeavingGuard } from 'components/routeLeavingGuard';

export const VirtualTerminalForm: FC = () => {
  const dispatch = useDispatch();
  const isPaymentIdGeneratorEnabled = useSelector(
    (state: RootState) => state.user.isPaymentIdGeneratorEnabled
  );
  const menuVersion = useSelector(
    (state: RootState) => state.layoutConfiguration.menuVersion
  );
  const getInitialFields = useCallback(
    () => ({
      ...formStateFactory(fieldsConfig.general, isPaymentIdGeneratorEnabled),
      ...formStateFactory(fieldsConfig.additional),
    }),
    [isPaymentIdGeneratorEnabled]
  );
  const [isLoading, setLoading] = useState(false);
  const [fields, setFields] = useState<AnyObject>(() => getInitialFields());
  const [fieldsSnapshot, setFieldsSnapshot] = useState<AnyObject>(fields);
  const [fieldsState, setFieldsState] = useState({ paymentMethodCode: false });
  const [hiddenFields, setHiddenFields] = useState({ cardToken: true });
  const [paymentPageMethod, setPaymentPageMethod] = useState<Dictionary>({
    isFetched: false,
    isLoading: false,
    list: [],
    hasMoreRows: false,
  });
  const [motoType, setMotoType] = useState<Dictionary>({
    isFetched: false,
    isLoading: false,
    list: [],
    hasMoreRows: false,
  });
  const [validationErrors, setValidationErrors] = useState<AnyObject>({});
  const [status, setStatus] = useState<FormFieldsProps['status']>();
  const [isFieldsDirty, setFieldsDirty] = useState(false);
  const isOldMenu = menuVersion === 'old';

  useEffect(() => {
    getMoto();
  }, []);

  useEventListener(
    {
      events: [Messages.MotoPayment_Create],
      onEvent: (_, data) => {
        const {
          payload: { validationErrors, paymentPageUrl, motoPaymentId },
        } = data;

        if (validationErrors) {
          if (validationErrors.common) {
            setFieldsDirty(false);
            setStatus('terminalError');
          }

          setValidationErrors(validationErrors);
        } else {
          dispatch(
            openModal({
              modalId: 'PaymentFrame',
              content: {
                src: paymentPageUrl,
                motoPaymentId,
              },
              callback: ({ operationStatus: status }) => {
                setFieldsDirty(false);
                setStatus(status);
              },
            })
          );
        }

        setLoading(false);
      },
    },
    []
  );

  const getMoto = useCallback(async () => {
    setMotoType((prev) => ({ ...prev, isLoading: true }));

    try {
      const data = await loadDictionary({ name: 'motoType' }, false);
      const dictionary = data.elements.filter((item) => !!item.id);
      setMotoType({
        isLoading: false,
        isFetched: true,
        hasMoreRows: false,
        list: getCustomSelectItems({ list: dictionary }),
      });
    } catch (e) {
      setMotoType((prev) => ({ ...prev, isLoading: false, isFetched: false }));
    }
  }, []);

  const getPaymentMethods = useCallback(async (value: string) => {
    setPaymentPageMethod((prev) => ({ ...prev, isLoading: true }));

    try {
      const data = await loadDictionary(
        {
          name: 'paymentPageMethod',
          params: { projectId: value },
        },
        false
      );
      setPaymentPageMethod({
        list: getMethods(data.elements),
        isFetched: true,
        hasMoreRows: false,
        isLoading: false,
      });
      setFieldsState({ paymentMethodCode: true });
      setFields((prevFields) => ({ ...prevFields, paymentMethodCode: '' }));
    } catch (e) {
      setPaymentPageMethod((prev) => ({
        ...prev,
        isLoading: false,
        isFetched: false,
        hasMoreRows: false,
      }));
    }
  }, []);

  const handleChange = useCallback(
    (name: string, value: any) => {
      setFields((prevFields) => {
        const updatedFields = {
          ...prevFields,
          [name]: value,
        };

        if (name === 'paymentMethodCode') {
          const isCardToken = value === 'card-token';
          updatedFields.cardToken = isCardToken ? prevFields.cardToken : '';
          setHiddenFields({ cardToken: !isCardToken });
        }

        setFieldsDirty(!isEqual(updatedFields, fieldsSnapshot));

        return updatedFields;
      });

      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        [name]: '',
      }));

      if (name === 'projectId' && value) {
        getPaymentMethods(value);
      }
    },
    [fieldsSnapshot]
  );

  const hanldeSubmit = useCallback(async () => {
    setLoading(true);

    try {
      const payload = {
        ...fields,
        amount: Utils.getNumberWithoutSpace(fields.amount),
      };

      await createMoto(pickBy(payload, (prop) => prop !== undefined));
    } catch (e: any) {
      const { payload } = e;

      const withoutError =
        payload?.validationErrors ||
        (Array.isArray(payload) && !payload.length);

      if (withoutError) {
        return;
      }

      showNotification({
        status: 'error',
        content: payload,
      });
    } finally {
      setLoading(false);
    }
  }, [fields]);

  const handleReset = useCallback(() => {
    const initialFields = getInitialFields();
    setFields(initialFields);
    setFieldsSnapshot(initialFields);
    setStatus(undefined);
  }, [getInitialFields]);

  const isRequiredFieldsFilled = useMemo(() => {
    const requiredFields = getRequiredFields(fieldsConfig);
    let isCardToken = false;

    return requiredFields.every(({ id }) => {
      if (id === 'paymentMethodCode' && fields[id] === 'card-token') {
        isCardToken = true;
      }

      if (id === 'cardToken') {
        return isCardToken ? !!fields[id] : !fields[id];
      }

      if (id === 'motoType' && fields[id] === 0) {
        return true;
      }

      return !!fields[id];
    });
  }, [fields]);

  const submittedTitle = useMemo(() => {
    return status === 'success'
      ? 'payouts.request.moto.header'
      : 'payouts.request.moto.rejectedRequest.header';
  }, [status]);

  const submittedText = useMemo(() => {
    switch (status) {
      case 'success':
        return 'payouts.request.moto.success.text';
      case 'fail':
      case 'decline':
      case 'error':
        return 'payouts.request.single.timeout.text';
      case 'source error':
        return 'source error';
      case 'terminalError':
        return validationErrors.common;
      default:
        return 'unknown error';
    }
  }, [status, validationErrors]);

  return (
    <>
      <FormFields
        id='moto'
        title={isOldMenu ? undefined : 'payouts.request.tabs.MOTO'}
        text='payouts.request.moto.infoText'
        generalTitle='remittance.addPayout.generalInfo.label'
        additionalTitle='remittance.addPayout.additionalInfo.label'
        backUrl={isOldMenu ? '/payouts' : undefined}
        backText='payouts.request.backBtn'
        repeatBackUrl={isOldMenu ? '/manual-payments' : '/payments'}
        repeatBackText={
          isOldMenu ? undefined : 'virtualTerminal.form.secondary.button.label'
        }
        createButtonText='payouts.request.payNow.button'
        repeatCreateButtonText='payouts.request.newRequestBtn'
        isLoading={isLoading}
        canCreate={isRequiredFieldsFilled}
        fieldsValues={fields}
        fieldsConfig={fieldsConfig}
        filtersValuesCustom={{ paymentPageMethod, motoType }}
        fieldsState={fieldsState}
        hiddenFields={hiddenFields}
        validationErrors={validationErrors}
        status={status}
        isSubmitted={Boolean(status)}
        submittedTitle={submittedTitle}
        submittedText={submittedText}
        onChange={handleChange}
        onSubmit={hanldeSubmit}
        onReset={handleReset}
      />

      <RouteLeavingGuard blockWhen={isFieldsDirty} />
    </>
  );
};
